/**
 * ExecuteStep - Step-level orchestration UI.
 *
 * Replaces the old fire-and-forget single-enum launchStatus with a per-step
 * progress view backed by POST /api/orchestration/:id/step/all and
 * GET /api/orchestration/:id polling.
 *
 * Create mode: runs ddb -> glue -> transform -> andes sequentially.
 * Append mode: only shows the transform step.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Container from '@amzn/awsui-components-console/container';
import Header from '@amzn/awsui-components-console/header';
import SpaceBetween from '@amzn/awsui-components-console/space-between';
import Box from '@amzn/awsui-components-console/box';
import Alert from '@amzn/awsui-components-console/alert';
import Button from '@amzn/awsui-components-console/button';
import StatusIndicator from '@amzn/awsui-components-console/status-indicator';
import KeyValuePairs from '@amzn/awsui-components-console/key-value-pairs';
import ColumnLayout from '@amzn/awsui-components-console/column-layout';
import Modal from '@amzn/awsui-components-console/modal';
import ExpandableSection from '@amzn/awsui-components-console/expandable-section';
import Link from '@amzn/awsui-components-console/link';
import { useNavigate } from 'react-router-dom';
import type {
  DatasetConfig,
  DatasetOrchestration,
  OrchestrationStepName,
  OrchestrationStepStatus,
} from '../../../../types/starnexus';
import { apiUrl, apiFetch, apiFetchDefaults } from '../../../../utils/apiClient';

// ============================================================================
// Constants
// ============================================================================

interface StepMeta {
  key: OrchestrationStepName;
  title: string;
  description: string;
}

const STEPS: StepMeta[] = [
  { key: 'ddb', title: '1. Dataset Record', description: 'Persist dataset metadata to DynamoDB' },
  { key: 'glue', title: '2. Glue Table', description: 'Create Glue catalog table for query via Athena' },
  { key: 'transform', title: '3. Transform & S3 Data', description: 'Run ETL job, write Parquet files to S3' },
  { key: 'andes', title: '4. Andes Table', description: 'Create Andes table for downstream consumers' },
  { key: 'datacraft', title: '5. DataCraft Pipeline', description: 'Create DataCraft pipeline for Cairns ingestion' },
];

const APPEND_STEPS: StepMeta[] = [
  { key: 'transform', title: '1. Transform & S3 Data', description: 'Run ETL job, write Parquet files to S3' },
];

const STATUS_INDICATOR_TYPE: Record<OrchestrationStepStatus | 'pending', 'pending' | 'in-progress' | 'success' | 'error' | 'stopped'> = {
  pending: 'pending',
  in_progress: 'in-progress',
  success: 'success',
  failed: 'error',
  skipped: 'stopped',
};

// ============================================================================
// Props
// ============================================================================

interface ExecuteStepProps {
  config: DatasetConfig;
  datasetId: string | null;
  fileId: string | null;
  onComplete: (passed: boolean) => void;
  mode?: 'create' | 'append';
  batchId?: string;
}

// ============================================================================
// Helpers
// ============================================================================

function ts(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function isTerminal(status: OrchestrationStepStatus | undefined): boolean {
  return status === 'success' || status === 'failed' || status === 'skipped';
}

function allTerminal(orch: DatasetOrchestration | null, steps: StepMeta[]): boolean {
  if (!orch) return false;
  return steps.every(s => isTerminal(orch[s.key]?.status));
}

function hasFailedStep(orch: DatasetOrchestration | null, steps: StepMeta[]): boolean {
  if (!orch) return false;
  return steps.some(s => orch[s.key]?.status === 'failed');
}

function allSuccess(orch: DatasetOrchestration | null, steps: StepMeta[]): boolean {
  if (!orch) return false;
  return steps.every(s => {
    const st = orch[s.key]?.status;
    return st === 'success' || st === 'skipped';
  });
}

// ============================================================================
// StepRow component
// ============================================================================

interface StepRowProps {
  title: string;
  description: string;
  status: OrchestrationStepStatus;
  error?: string;
  message?: string;
  meta?: Record<string, any>;
  onRetry?: () => void;
}

const StepRow: React.FC<StepRowProps> = ({ title, description, status, error, message, meta, onRetry }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '8px 0', borderBottom: '1px solid #e9ebed' }}>
    <Box padding={{ top: 'xxs' }}>
      <StatusIndicator type={STATUS_INDICATOR_TYPE[status] || 'pending'}>
        {status === 'in_progress' ? 'In Progress' : status === 'success' ? 'Success' : status === 'failed' ? 'Failed' : status === 'skipped' ? 'Skipped' : 'Pending'}
      </StatusIndicator>
    </Box>
    <div style={{ flex: 1 }}>
      <Box variant="h4">{title}</Box>
      <Box color="text-body-secondary" fontSize="body-s">{description}</Box>
      {status === 'in_progress' && message && (
        <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxs' }}>
          {message}
        </Box>
      )}
      {status === 'failed' && error && (
        <ExpandableSection headerText="Error details" variant="footer">
          <Box color="text-status-error" fontSize="body-s">
            <code style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{error}</code>
          </Box>
        </ExpandableSection>
      )}
      {status === 'skipped' && meta?.reason && (
        <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxs' }}>
          Skipped: {meta.reason}
        </Box>
      )}
      {meta && Object.keys(meta).length > 0 && (
        <Box fontSize="body-s" color="text-body-secondary" margin={{ top: 'xxs' }}>
          {meta.jobRunId && <>Job run: <code>{meta.jobRunId}</code> </>}
          {meta.tableArn && <>Table ARN: <code>{meta.tableArn}</code> </>}
          {meta.glueTable && <>Glue: <code>{meta.glueTable}</code> </>}
          {meta.pipelineName && <>Pipeline: <code>{meta.pipelineName}</code> </>}
          {meta.consoleUrl && <Link href={meta.consoleUrl} external>Open in console</Link>}
        </Box>
      )}
    </div>
    {onRetry && (
      <Button variant="inline-link" onClick={onRetry}>Retry</Button>
    )}
  </div>
);

// ============================================================================
// Main component
// ============================================================================

export const ExecuteStep: React.FC<ExecuteStepProps> = ({ config, datasetId, fileId, onComplete, mode = 'create', batchId }) => {
  const navigate = useNavigate();
  const isAppend = mode === 'append';
  const steps = isAppend ? APPEND_STEPS : STEPS;

  const [orchestration, setOrchestration] = useState<DatasetOrchestration | null>(null);
  const [launching, setLaunching] = useState(false);
  const [launchError, setLaunchError] = useState<string | null>(null);
  const [launched, setLaunched] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);

  // Conflict detection (create mode only)
  const [checkingConflict, setCheckingConflict] = useState(false);
  const [conflictModalVisible, setConflictModalVisible] = useState(false);
  const [conflictData, setConflictData] = useState<{
    count: number;
    datasets: Array<{ datasetId: string; datasetName: string; owner: string; teamName: string; createdAt: string; glueTableName: string; status?: string }>;
  } | null>(null);

  const logEndRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const appendLog = useCallback((msg: string) => {
    setLogs(prev => [...prev, `[${ts()}] ${msg}`]);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [logs]);

  // Cleanup polling on unmount
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // ---- Polling ----
  const prevStatusRef = useRef<Record<string, string>>({});
  const fetchOrchestration = useCallback(async () => {
    if (!datasetId) return;
    try {
      const r = await apiFetch(apiUrl(`/api/orchestration/${encodeURIComponent(datasetId)}`), { ...apiFetchDefaults });
      if (!r.ok) {
        if (import.meta.env?.DEV) {
          console.warn(`[ExecuteStep] poll GET /orchestration/${datasetId} -> ${r.status}`);
        }
        return;
      }
      const data = await r.json();
      // API returns { orchestration: {...}, derivedStatus, nextStep, dataset }
      // Extract the nested orchestration object for step status tracking
      const orch: DatasetOrchestration = data.orchestration || data;
      setOrchestration(orch);

      // Log step status changes
      for (const s of steps) {
        const newStatus = orch?.[s.key]?.status;
        const prevStatus = prevStatusRef.current[s.key];
        if (newStatus && newStatus !== prevStatus) {
          prevStatusRef.current[s.key] = newStatus;
          if (newStatus === 'success') {
            const meta = orch?.[s.key]?.meta;
            const extra = meta?.glueTable ? ` (${meta.glueTable})` : meta?.pipelineName ? ` (${meta.pipelineName})` : meta?.tableArn ? ` (${meta.tableArn.split('/').pop()})` : '';
            appendLog(`✅ ${s.title} — completed${extra}`);
          } else if (newStatus === 'failed') {
            const msg = orch?.[s.key]?.message || orch?.[s.key]?.error;
            appendLog(`❌ ${s.title} — failed${msg ? `: ${msg}` : ''}`);
          } else if (newStatus === 'in_progress') {
            const msg = orch?.[s.key]?.message;
            appendLog(`⏳ ${s.title} — in progress${msg ? ` (${msg})` : '...'}`);
          }
        }
      }

      if (allTerminal(orch, steps)) {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (allSuccess(orch, steps)) {
          appendLog('All steps completed successfully.');
          // Auto-activate: set status to active so user doesn't need to click Done
          if (datasetId && mode === 'create') {
            apiFetch(apiUrl(`/api/datasets/${encodeURIComponent(datasetId)}/status`), {
              ...apiFetchDefaults,
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ status: 'active' }),
            }).then(() => appendLog('Dataset status → active')).catch(() => {});
          }
          onComplete(true);
        } else {
          appendLog('Some steps failed. Use Retry to re-run failed steps.');
        }
      }
    } catch { /* silent */ }
  }, [datasetId, steps, appendLog, onComplete]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    fetchOrchestration();
    pollRef.current = setInterval(fetchOrchestration, 10000);
  }, [fetchOrchestration]);

  // ---- Conflict check ----
  const checkConflict = async (): Promise<boolean> => {
    if (isAppend) return false;
    const { provider, tableName } = config.andesSettings;
    if (!provider || !tableName) return false;
    setCheckingConflict(true);
    try {
      // excludeDatasetId: skip the wizard's own in-progress draft record, which was auto-saved by
      // saveDraft on the way to step 4 and would otherwise match itself as a false-positive conflict.
      const excludeParam = datasetId ? `&excludeDatasetId=${encodeURIComponent(datasetId)}` : '';
      const r = await apiFetch(apiUrl(`/api/datasets/check-conflict?providerName=${encodeURIComponent(provider)}&tableName=${encodeURIComponent(tableName)}${excludeParam}`), { ...apiFetchDefaults });
      const data = await r.json();
      if (data.exists && data.count > 0) {
        setConflictData({ count: data.count, datasets: data.datasets });
        setConflictModalVisible(true);
        return true;
      }
      return false;
    } catch (e: any) {
      appendLog(`Conflict probe failed: ${e.message}. Proceeding anyway.`);
      return false;
    } finally {
      setCheckingConflict(false);
    }
  };

  // ---- Launch ----
  const launch = async () => {
    if (!datasetId || !fileId) { setLaunchError('Missing datasetId or fileId'); return; }
    if (!isAppend && !config.basicSettings.teamId) {
      setLaunchError('Team is required. Return to Basic Settings and select a team.');
      return;
    }
    setLaunching(true);
    setLaunchError(null);
    setLogs([]);
    appendLog('Starting orchestration...');

    try {
      const body: Record<string, any> = {
        datasetId,
        fileId,
        mode,
        config: {
          basicSettings: config.basicSettings,
          schemaConfig: config.schemaConfig,
          andesSettings: config.andesSettings,
          outputConfig: config.outputConfig,
          sheetName: config.basicSettings?.sheetName || undefined,
        },
        batchId: batchId || undefined,
      };

      const endpoint = isAppend
        ? `/api/orchestration/${encodeURIComponent(datasetId)}/step/transform`
        : `/api/orchestration/${encodeURIComponent(datasetId)}/step/all`;

      const r = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const responseBody = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
      if (!r.ok) {
        throw new Error(responseBody.error || responseBody.message || `Orchestration failed: HTTP ${r.status}`);
      }
      // Body-level error check: the server returns 200 with { errors: [...] } or { failedAt } when
      // individual steps fail but the /step/all envelope succeeds. Treat those as launch failures
      // so the user sees a toast instead of a silently stuck pending state.
      if (responseBody.failedAt || (Array.isArray(responseBody.errors) && responseBody.errors.length > 0)) {
        const firstErr = responseBody.errors?.[0]?.error || `Step ${responseBody.failedAt} failed`;
        throw new Error(firstErr);
      }

      appendLog('Orchestration started. Polling for progress...');
      setLaunched(true);
      startPolling();
    } catch (e: any) {
      appendLog(`FAILED: ${e.message}`);
      setLaunchError(e.message);
    } finally {
      setLaunching(false);
    }
  };

  const handleCreateClick = async () => {
    const blocked = await checkConflict();
    if (!blocked) launch();
  };

  // ---- Retry ----
  const handleRetryStep = async (stepName: OrchestrationStepName) => {
    if (!datasetId) return;
    appendLog(`Retrying step: ${stepName}...`);
    try {
      const r = await apiFetch(apiUrl(`/api/orchestration/${encodeURIComponent(datasetId)}/step/${stepName}`), {
      ...apiFetchDefaults,
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          datasetId, fileId, mode,
          config: {
            basicSettings: config.basicSettings,
            schemaConfig: config.schemaConfig,
            andesSettings: config.andesSettings,
            outputConfig: config.outputConfig,
          },
          batchId: batchId || undefined,
        }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ error: `HTTP ${r.status}` }));
        appendLog(`Retry failed: ${err.error || err.message}`);
        return;
      }
      appendLog(`Step ${stepName} re-triggered. Polling...`);
      startPolling();
    } catch (e: any) {
      appendLog(`Retry error: ${e.message}`);
    }
  };

  const handleRetryAll = () => {
    steps.filter(s => orchestration?.[s.key]?.status === 'failed').forEach(s => handleRetryStep(s.key));
  };

  const goToDatasetPage = () => {
    if (datasetId) navigate(`/starnexus/uploader/datasets/${datasetId}`);
  };

  const hasFailed = hasFailedStep(orchestration, steps);
  const isDone = orchestration ? allTerminal(orchestration, steps) : false;
  const isSuccess = orchestration ? allSuccess(orchestration, steps) : false;
  const isIdle = !launched && !launching;

  return (
    <SpaceBetween size="l">
      {/* Configuration Summary */}
      <Container header={<Header variant="h2">{isAppend ? 'Upload new snapshot - summary' : 'Configuration Summary'}</Header>}>
        <ColumnLayout columns={2} variant="text-grid">
          <KeyValuePairs items={[
            { label: 'Dataset name', value: config.basicSettings.datasetName || '-' },
            { label: 'Team', value: config.basicSettings.teamId || '-' },
            { label: 'Mode', value: mode.charAt(0).toUpperCase() + mode.slice(1) },
            { label: 'Schema Columns', value: String(config.schemaConfig.length) },
          ]} />
          <KeyValuePairs items={[
            { label: 'Andes Provider', value: config.andesSettings.provider || '-' },
            { label: 'Andes Table', value: config.andesSettings.tableName || '-' },
            { label: 'Load Mode', value: config.outputConfig.loadMode || '-' },
            { label: 'Partition', value: 'snapshot_date' },
          ]} />
        </ColumnLayout>
      </Container>

      {/* Creation progress - step list */}
      <Container header={<Header variant="h2">Creation Progress</Header>}>
        <SpaceBetween size="m">
          {isIdle && (
            <Alert type="info">
              Click <Box variant="strong" display="inline">{isAppend ? 'Launch append' : 'Create dataset'}</Box> to begin.
              Each step runs sequentially with skip-if-already-succeeded logic.
            </Alert>
          )}

          {launched && (
            <SpaceBetween size="s">
              {steps.map(stepMeta => {
                const step = orchestration?.[stepMeta.key];
                const status: OrchestrationStepStatus = step?.status || 'pending';
                return (
                  <StepRow
                    key={stepMeta.key}
                    title={stepMeta.title}
                    description={stepMeta.description}
                    status={status}
                    error={step?.error}
                    message={step?.message}
                    meta={step?.meta}
                    onRetry={status === 'failed' ? () => handleRetryStep(stepMeta.key) : undefined}
                  />
                );
              })}
            </SpaceBetween>
          )}

          {hasFailed && isDone && (
            <Button variant="primary" onClick={handleRetryAll}>Retry all failed steps</Button>
          )}

          {isSuccess && (
            <Alert type="success" header="All steps completed">
              {isAppend
                ? 'New snapshot uploaded successfully.'
                : `Dataset ${config.basicSettings.datasetName} created. View it on the dataset page.`}
            </Alert>
          )}

          {launchError && (
            <Alert type="error" header="Launch failed">{launchError}</Alert>
          )}

          {/* Action buttons */}
          <Box textAlign="right">
            <SpaceBetween direction="horizontal" size="xs">
              {isSuccess && (
                <Button variant="primary" iconName="external" onClick={goToDatasetPage}>Go to dataset page</Button>
              )}
              {isIdle && (
                <Button
                  variant="primary"
                  loading={checkingConflict}
                  disabled={checkingConflict}
                  onClick={handleCreateClick}
                >
                  {checkingConflict ? 'Checking...' : isAppend ? 'Launch append' : 'Create dataset'}
                </Button>
              )}
              {launching && (
                <Button variant="primary" loading disabled>Launching...</Button>
              )}
              {hasFailed && isDone && (
                <Button onClick={handleCreateClick}>Retry from scratch</Button>
              )}
            </SpaceBetween>
          </Box>
        </SpaceBetween>
      </Container>

      {/* Technical Logs */}
      {logs.length > 0 && (
        <Container header={<Header variant="h3">Technical Logs</Header>}>
          <div
            style={{
              backgroundColor: '#1e1e1e',
              color: '#d4d4d4',
              fontFamily: 'Menlo, Monaco, "Courier New", monospace',
              fontSize: '12px',
              lineHeight: '1.5',
              padding: '12px 16px',
              borderRadius: '4px',
              maxHeight: '320px',
              overflowY: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {logs.map((line, i) => <div key={i}>{line}</div>)}
            <div ref={logEndRef} />
          </div>
        </Container>
      )}

      {/* Conflict modal (create mode only) */}
      <Modal
        visible={conflictModalVisible}
        onDismiss={() => setConflictModalVisible(false)}
        header={`Dataset already exists: ${config.andesSettings.provider}.${config.andesSettings.tableName}`}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={() => setConflictModalVisible(false)}>Cancel this wizard</Button>
              <Button
                variant="primary"
                iconName="external"
                onClick={() => {
                  const existing = conflictData?.datasets[0];
                  if (existing) navigate(`/starnexus/uploader/datasets/${existing.datasetId}`);
                }}
              >
                Go to existing dataset
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info" header="Looks like you want to add new data to an existing dataset">
            This Andes table already exists. To add new data, use <Box variant="strong" display="inline">"Upload new snapshot"</Box> on the existing dataset's detail page.
          </Alert>
          {conflictData?.datasets.map(ds => (
            <Box key={ds.datasetId} padding="s" fontSize="body-s">
              <Box variant="awsui-key-label">
                {ds.datasetName} <code style={{ fontSize: '11px' }}>({ds.datasetId})</code>
              </Box>
              <Box color="text-body-secondary">
                Owner: {ds.owner} - Team: {ds.teamName} - Created: {new Date(ds.createdAt).toLocaleString()}
                <br />Glue table: <code>{ds.glueTableName}</code>
                <br />Status: <Box variant="strong" display="inline">{ds.status}</Box>
              </Box>
            </Box>
          ))}
          <Alert type="warning" header="To replace the existing dataset">
            Cancel this wizard, go to the existing dataset's Settings page and delete it first.
          </Alert>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
};

export default ExecuteStep;
